import * as HLF from "html-living-framework";
import * as SpringRoll from "springroll";
import type { CaptionToken } from "../data/CaptionToken";
import type { BaseCaptionRenderer } from "../gameobjects/BaseCaptionRenderer";

export class CaptionManager extends PIXI.Container {
    private _active: boolean;
    private readonly _data: SpringRoll.CaptionData;
    private _player: SpringRoll.CaptionPlayer | undefined;
    private _renderer: BaseCaptionRenderer | undefined;

    constructor(pData: SpringRoll.CaptionData) {
        super();
        this._active = true;
        this._data = pData;

        PubSub.subscribe(HLF.PLAY_CAPTION, this.onPlayCaptionRequested.bind(this));
        PubSub.subscribe(HLF.STOP_CAPTION, this.onStopCaptionRequested.bind(this));
    }

    public setRenderer(pRenderer: BaseCaptionRenderer): void {
        if (this._renderer !== undefined) {
            this.removeChild(this._renderer);
        }
        this._renderer = pRenderer;
        this._renderer.visible = this._active;
        this.addChild(this._renderer);
        if (this._player === undefined) {
            this._player = new SpringRoll.CaptionPlayer(this._data, this._renderer);
        }
        else {
            this._player.renderer = this._renderer;
        }
    }

    public get active(): boolean {
        return this._active;
    }

    public set active(pValue: boolean) {
        this._active = pValue;
        if (this._renderer !== undefined) {
            this._renderer.visible = this._active;
        }
    }

    public update(pDelta: number): void {
        if (this._player !== undefined && this._active === true) {
            this._player.update(pDelta);
        }
    }

    public playCaption(pId: string, pStart: number = 0, pArgs?: any): void {
        if (this._player !== undefined) {
            this._player.start(pId, pStart, pArgs);
        }
    }

    public stopCaption(): void {
        if (this._player !== undefined) {
            this._player.stop();
        }
    }

    public onResize(pSize: PIXI.Point): void {
        if (this._renderer !== undefined) {
            this._renderer.onResize(pSize);
        }
    }

    private onPlayCaptionRequested(pTopic: string, pToken: CaptionToken): void {
        if (pToken.args) {
            const keys = Object.keys(pToken.args);
            for (let i = 0; i < keys.length; i++) {
                // if content of variable is a caption id, add that caption's duration to this one
                const lastLine = this._player!.captions![pToken.id].lines[this._player!.captions![pToken.id].lines.length - 1];
                const origLastLine = this._data[pToken.id][this._data[pToken.id].length - 1];
                lastLine.endTime = origLastLine.end + this.getCaptionDuration(pToken.args[keys[i]]);
                // if content of variable is a caption id, replace with its own text
                pToken.args[keys[i]] = this.getCaptionText(pToken.args[keys[i]]);
            }
        }
        this.playCaption(pToken.id, pToken.start, pToken.args);
    }

    private onStopCaptionRequested(pTopic: string, pToken: CaptionToken): void {
        this.stopCaption();
    }

    private getCaptionText(pId: string) {
        return this._data[pId][0].content ?? pId;
    }

    private getCaptionDuration(pId: string) {
        return this._data[pId][0].end ?? 0;
    }
}
